import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { X, Loader2, Cable, Users, Split, Gauge, Waypoints, Box, Navigation, Pencil, Rows3, ChevronLeft, ChevronRight } from "lucide-react";
import { ClosureEditor } from "@/components/ClosureEditor";
import SpliceMap from "@/components/SpliceMap";
import { RouteMiniMap } from "@/components/RouteMiniMap";
import Client360Panel from "@/components/Client360Panel";

let _role = "";
try { _role = (JSON.parse(localStorage.getItem("fp_user") || "{}") || {}).role || ""; } catch { _role = ""; }

const STATUS_COLOR = { aktywny: "text-green-400", active: "text-green-400", nieaktywny: "text-slate-400", awaria: "text-red-400" };

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-sm font-semibold text-cyan-300 mb-2"><Icon size={16} /> {title}</div>
      {children}
    </div>
  );
}

export function ObjectInfo({ module, id, onClose, readOnly = false }) {
  const [nav, setNav] = useState({ module, id });
  const [stack, setStack] = useState([]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [edit, setEdit] = useState(false);
  const [client360Id, setClient360Id] = useState(null);
  const load = useCallback(() => {
    return api.get(`/scan/${nav.module}/${nav.id}`).then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || "Nie znaleziono obiektu"));
  }, [nav]);
  useEffect(() => { setNav({ module, id }); setStack([]); }, [module, id]);
  useEffect(() => {
    setData(null); setErr(""); setEdit(false);
    load();
  }, [nav, load]);
  const openObj = useCallback((m, oid) => {
    if (!m || !oid) return;
    setStack((s) => [...s, nav]);
    setNav({ module: m, id: oid });
  }, [nav]);
  const goBack = useCallback(() => {
    setStack((s) => { const cp = [...s]; const prev = cp.pop(); if (prev) setNav(prev); return cp; });
  }, []);
  const openClient = useCallback((cid) => { if (cid) setClient360Id(cid); }, []);

  return (
    <div className="fixed inset-0 z-[3000] bg-slate-950 text-slate-100 flex flex-col" data-testid="object-info">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800 bg-slate-900">
        {stack.length > 0 && <button onClick={goBack} data-testid="object-info-back" className="text-slate-400 hover:text-white"><ChevronLeft size={22} /></button>}
        <Box size={18} className="text-cyan-400" />
        <div className="flex-1 min-w-0">
          <div className="font-bold truncate" data-testid="object-info-title">{data?.title || (err ? "Błąd" : "Ładowanie…")}</div>
          {data?.subtitle && <div className="text-xs text-slate-400 truncate">{data.subtitle}</div>}
        </div>
        {onClose && <button onClick={onClose} data-testid="object-info-close" className="text-slate-400 hover:text-white"><X size={22} /></button>}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {!data && !err && <div className="flex items-center gap-2 text-slate-400 py-10 justify-center"><Loader2 className="animate-spin" size={18} /> Ładowanie…</div>}
        {err && <div className="text-red-300 bg-red-500/10 border border-red-500/30 rounded p-3 text-sm" data-testid="object-info-error">{err}</div>}

        {data && (
          <>
            {data.lat != null && (
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${data.lat},${data.lng}`} target="_blank" rel="noreferrer" data-testid="object-info-navigate"
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 rounded-lg px-4 py-3 font-semibold text-white">
                <Navigation size={18} /> Prowadź do (nawigacja GPS)
              </a>
            )}

            {data.geometry?.length >= 2 && (
              <RouteMiniMap geometry={data.geometry} closures={data.closures || []} length={data.length_m} />
            )}

            {data.fields?.length > 0 && (
              <div className="grid grid-cols-2 gap-2" data-testid="object-info-fields">
                {data.fields.filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
                  <div key={k} className="bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-2">
                    <div className="text-[11px] text-slate-500 uppercase tracking-wide">{k}</div>
                    <div className="text-sm font-medium break-words">{String(v)}</div>
                  </div>
                ))}
              </div>
            )}

            {data.splitters?.length > 0 && data.splitters.map((sp) => (
              <Section key={sp.id || sp.number} icon={Split} title={`Spliter ${sp.number || ""} · ${sp.split_type}`} testid={`object-info-splitter-${sp.id || sp.number}`}>
                <div className="flex flex-wrap gap-2 mb-2 text-xs">
                  <span className="bg-red-500/20 text-red-300 rounded-full px-2 py-0.5">Zajęte: {sp.occupied}</span>
                  <span className="bg-green-500/20 text-green-300 rounded-full px-2 py-0.5">Wolne: {sp.free}</span>
                  <span className="bg-slate-600/40 text-slate-300 rounded-full px-2 py-0.5">Razem: {sp.total}</span>
                </div>
                {sp.power && (
                  <div className="text-xs text-slate-300 bg-slate-900/60 rounded-lg p-2 mb-2 flex items-start gap-2" data-testid="object-info-power">
                    <Gauge size={14} className="text-amber-400 mt-0.5 shrink-0" />
                    <div>
                      Teoretyczna moc na wyjściu: <b className={sp.power.output_dbm >= sp.power.min_rx_dbm ? "text-green-400" : "text-red-400"}>{sp.power.output_dbm} dBm</b>
                      <div className="text-slate-500">Tx {sp.power.olt_tx_dbm} dBm − straty splitera {sp.power.split_loss_db} dB − złącza {sp.power.connectors_db} dB · zapas do min. Rx ({sp.power.min_rx_dbm}): <b className="text-slate-300">{sp.power.reserve_db} dB</b></div>
                    </div>
                  </div>
                )}
                {sp.clients?.length > 0 ? (
                  <div className="space-y-1">
                    {sp.clients.map((c, i) => (
                      <button key={i} onClick={() => c.id && openClient(c.id)} disabled={!c.id}
                        className={`w-full flex items-center gap-2 text-sm bg-slate-900/40 rounded px-2 py-1 text-left ${c.id ? "hover:bg-slate-800 cursor-pointer" : ""}`} data-testid={`object-info-splitter-client-${i}`}>
                        <span className="text-[10px] bg-cyan-600/30 text-cyan-200 rounded px-1.5 py-0.5 font-mono shrink-0">P{c.port ?? "?"}</span>
                        <span className="flex-1 truncate">{c.name || "—"}</span>
                        {c.status && <span className={`text-[11px] ${STATUS_COLOR[c.status] || "text-slate-400"}`}>● {c.status}</span>}
                        {c.id && <ChevronRight size={14} className="text-slate-500 shrink-0" />}
                      </button>
                    ))}
                  </div>
                ) : <div className="text-xs text-slate-500">Brak podłączonych klientów.</div>}
              </Section>
            ))}

            {data.panels?.length > 0 && (
              <Section icon={Rows3} title={`Przełącznice w szafie (${data.panels.length})`} testid="object-info-panels">
                <div className="space-y-1">
                  {data.panels.map((p, i) => (
                    <button key={p.id || i} onClick={() => p.id && openObj("patchpanels", p.id)} disabled={!p.id}
                      className={`w-full flex items-center gap-2 text-sm bg-slate-900/40 rounded px-2 py-1 text-left ${p.id ? "hover:bg-slate-800 cursor-pointer" : ""}`} data-testid={`object-info-panel-${p.id || i}`}>
                      <Rows3 size={14} className="text-emerald-300 shrink-0" />
                      <span className="flex-1 truncate">{p.number || "—"}</span>
                      <span className="text-xs text-slate-400">{p.used ?? 0}/{p.port_count ?? "?"} portów</span>
                      {p.id && <ChevronRight size={14} className="text-slate-500 shrink-0" />}
                    </button>
                  ))}
                </div>
              </Section>
            )}

            {data.ports?.length > 0 && (
              <Section icon={Cable} title={`Zajęte porty (${data.ports.length})`} testid="object-info-ports">
                <div className="space-y-1">
                  {data.ports.map((p, i) => {
                    const canOpen = !!(p.target_module && p.target_id);
                    return (
                    <button key={i} onClick={() => canOpen && openObj(p.target_module, p.target_id)} disabled={!canOpen}
                      className={`w-full flex items-center gap-2 text-sm bg-slate-900/40 rounded px-2 py-1 text-left ${canOpen ? "hover:bg-slate-800 cursor-pointer" : ""}`} data-testid={`object-info-port-${i}`}>
                      <span className="text-[10px] bg-cyan-600/30 text-cyan-200 rounded px-1.5 py-0.5 font-mono shrink-0">P{p.port ?? "?"}</span>
                      <span className="flex-1 truncate">{p.label || "—"}{p.target_port ? ` · port ${p.target_port}` : ""}</span>
                      {p.color && <span className="text-[11px] text-slate-400 shrink-0">{p.color}</span>}
                      {canOpen && <ChevronRight size={14} className="text-slate-500 shrink-0" />}
                    </button>
                    );
                  })}
                </div>
              </Section>
            )}

            {data.port_grid?.length > 0 && (
              <Section icon={Split} title={`Porty (${data.port_grid.length})`} testid="object-info-portgrid">
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                  {data.port_grid.map((p) => {
                    const act = p.client_id ? () => openClient(p.client_id)
                      : (p.splitter_ids?.length) ? () => openObj("splitters", p.splitter_ids[0])
                      : (p.target_module && p.target_id) ? () => openObj(p.target_module, p.target_id) : null;
                    return (
                    <button key={p.port} data-testid={`portgrid-${p.port}`} onClick={act || undefined} disabled={!act}
                      className={`rounded-lg px-1.5 py-1 text-center border ${p.occupied ? "bg-red-500/15 border-red-500/40" : "bg-green-500/15 border-green-500/40"} ${act ? "hover:ring-1 hover:ring-cyan-400 cursor-pointer" : ""}`}>
                      <div className="text-[10px] font-mono text-slate-300">P{p.port}</div>
                      <div className={`text-[10px] truncate ${p.occupied ? "text-red-300" : "text-green-300"}`}>
                        {p.occupied ? (p.client || p.label || (p.splitters?.length ? `SPL ${p.splitters[0]}` : "zajęty")) : "wolny"}
                      </div>
                    </button>
                    );
                  })}
                </div>
              </Section>
            )}

            {data.closures?.length > 0 && (
              <Section icon={Box} title={`Mufy na trasie (${data.closures.length})`} testid="object-info-closures">
                <div className="space-y-1">
                  {data.closures.map((c, i) => (
                    <button key={c.id || i} onClick={() => c.id && openObj("closures", c.id)} disabled={!c.id}
                      className={`w-full flex items-center gap-2 text-sm bg-slate-900/40 rounded px-2 py-1 text-left ${c.id ? "hover:bg-slate-800 cursor-pointer" : ""}`} data-testid={`object-info-closure-${c.id || i}`}>
                      <Box size={14} className="text-amber-300 shrink-0" />
                      <span className="flex-1 truncate">{c.number || "Mufa"}</span>
                      {c.role && <span className="text-[11px] text-slate-400 shrink-0">{c.role}</span>}
                      {c.id && <ChevronRight size={14} className="text-slate-500 shrink-0" />}
                    </button>
                  ))}
                </div>
              </Section>
            )}

            {data.cables?.length > 0 && (
              <Section icon={Cable} title={`Kable (${data.cables.length})`} testid="object-info-cables">
                <div className="space-y-1">
                  {data.cables.map((cb, i) => (
                    <button key={i} onClick={() => cb.id && openObj("cables", cb.id)} disabled={!cb.id}
                      className={`w-full flex items-center gap-2 text-sm bg-slate-900/40 rounded px-2 py-1 text-left ${cb.id ? "hover:bg-slate-800 cursor-pointer" : ""}`} data-testid={`object-info-cable-${cb.id || i}`}>
                      <Cable size={14} className="text-cyan-300 shrink-0" />
                      <span className="flex-1 truncate">{cb.number || "—"}</span>
                      {cb.fiber_count != null && <span className="text-xs text-slate-400">{cb.fiber_count} wł.{cb.type ? ` · ${cb.type}` : ""}</span>}
                      {cb.id && <ChevronRight size={14} className="text-slate-500 shrink-0" />}
                    </button>
                  ))}
                </div>
              </Section>
            )}

            {data.clients?.length > 0 && !data.splitters?.length && (
              <Section icon={Users} title={`Klienci (${data.clients.length})`} testid="object-info-clients">
                <div className="space-y-1">
                  {data.clients.map((c, i) => (
                    <button key={i} onClick={() => c.id && openClient(c.id)} disabled={!c.id}
                      className={`w-full flex items-center gap-2 text-sm bg-slate-900/40 rounded px-2 py-1 text-left ${c.id ? "hover:bg-slate-800 cursor-pointer" : ""}`} data-testid={`object-info-client-${i}`}>
                      {c.port != null && <span className="text-[10px] bg-cyan-600/30 text-cyan-200 rounded px-1.5 py-0.5 font-mono shrink-0">P{c.port}</span>}
                      <span className="flex-1 truncate">{c.name || "—"}</span>
                      {c.status && <span className={`text-[11px] ${STATUS_COLOR[c.status] || "text-slate-400"}`}>● {c.status}</span>}
                      {c.id && <ChevronRight size={14} className="text-slate-500 shrink-0" />}
                    </button>
                  ))}
                </div>
              </Section>
            )}

            {data.flow?.length > 0 && (
              <Section icon={Waypoints} title={`Rozpływ włókien (${data.flow.length} spawów)`} testid="object-info-flow">
                <div className="space-y-0.5 text-xs font-mono max-h-64 overflow-y-auto">
                  {data.flow.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-slate-900/40 rounded px-2 py-1">
                      <span className="text-slate-400 truncate">{f.cable_number || "kabel ?"}</span>
                      <span className="text-cyan-300">wł.{f.fiber_in ?? "?"}</span>
                      <span className="text-slate-500">→</span>
                      <span className="text-green-300">{f.target_type === "Splitter" ? "SPLITER" : `${f.cable_out_number ? f.cable_out_number + " " : ""}wł.${f.fiber_out ?? "?"}`}</span>
                      {f.tray != null && <span className="ml-auto text-slate-500">taca {f.tray}</span>}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {data.module === "closures" && (
              <div className="overflow-x-auto" data-testid="closure-splicemap">
                <SpliceMap closureId={data.id} closureNumber={data.title} readOnly={readOnly} />
              </div>
            )}

            {data.module === "closures" && !readOnly && _role !== "odczyt" && (
              <div data-testid="closure-edit-section">
                <button onClick={() => setEdit((e) => !e)} data-testid="closure-edit-toggle"
                  className={`w-full flex items-center justify-center gap-2 rounded-lg px-4 py-3 font-semibold ${edit ? "bg-slate-700 text-white" : "bg-amber-600 hover:bg-amber-500 text-white"}`}>
                  <Pencil size={16} /> {edit ? "Zamknij tryb edycji mufy" : "Tryb edycji mufy (spawy / cięcie)"}
                </button>
                {edit && <ClosureEditor closureId={data.id} closureNumber={data.title} closureLat={data.lat} closureLng={data.lng} flow={data.flow || []} onChanged={load} />}
              </div>
            )}

            {data.all_fields?.length > 0 && (
              <details className="bg-slate-800/60 border border-slate-700 rounded-xl p-3" data-testid="object-info-more">
                <summary className="text-sm font-semibold text-cyan-300 cursor-pointer select-none">Więcej informacji ({data.all_fields.length})</summary>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {data.all_fields.map(([k, v], i) => (
                    <div key={i} className="bg-slate-900/40 border border-slate-700 rounded-lg px-3 py-2">
                      <div className="text-[11px] text-slate-500 uppercase tracking-wide break-words">{k}</div>
                      <div className="text-sm font-medium break-words">{String(v)}</div>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </div>
      {client360Id && (
        <div className="fixed inset-0 z-[3100] bg-slate-950" data-testid="object-info-client360">
          <Client360Panel initialClientId={client360Id} onClose={() => setClient360Id(null)} />
        </div>
      )}
    </div>
  );
}
